import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { DateOfBirthPicker } from '@/components/ui/date-of-birth-picker';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { formatDateTimeWithTimezone } from '@/utils/timezoneUtils';
import {
  SquarePen,
  RefreshCw,
  CalendarIcon,
  Search,
  Filter,
  Menu,
  Play,
  Pause
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCareSpecialistAssignments } from '@/hooks/cs/useCareSpecialistAssignments.ts';
import { useCareSpecialistStatus } from '@/hooks/cs/useCareSpecialistStatus.ts';
import UnifiedHeader from '@/components/common/UnifiedHeader';
import GlobalSearch from '@/components/features/search/GlobalSearch';
import NotificationBell from '@/components/common/NotificationBell.tsx';
import { serviceFactory } from '@/services/core/serviceFactory';
import { CareSpecialistService } from '@/services/features/CareSpecialistService';
import { badgeStyles, getCCMStatusStyle, getRPMStatusStyle } from '@/utils/badgeStyles';
import PaginationComponent from '@/components/common/PaginationComponent';
import CSSearchForm from '@/components/cs/CSSearchForm.tsx';
import StatusSelectionComponent from '@/components/common/StatusSelectionComponent';

interface Patient {
  pep_patient_id: number;
  patient_clinic_data_id: number;
  full_name: string;
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
  consented_appointment_date_time: string;
  dob: string;
  provider_name: string;
  mobile_phone_number: string;
  ccm_status_value: string;
  rpm_status_value: string;
}

interface StatusOption {
  id: number;
  status: string;
}

type ViewType = 'main' | 'followup' | 'deferred' | 'search';

interface CSPatientListProps {
  onPatientSelect?: (patientClinicDataId: number, clinicId?: number) => void;
}

const CSPatientList = ({ onPatientSelect }: CSPatientListProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasuraToken } = useAuth();
  const { toast } = useToast();
  const { clinicIds, isLoading: clinicIdsLoading } = useCareSpecialistAssignments();
  const { deferredTask, followUp } = useCareSpecialistStatus();

  const [currentView, setCurrentView] = useState<ViewType>('main');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState('name');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter state
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedDateTime, setSelectedDateTime] = useState<Date>();
  const [selectedCCMStatuses, setSelectedCCMStatuses] = useState<string[]>([]);
  const [selectedRPMStatuses, setSelectedRPMStatuses] = useState<string[]>([]);
  const [ccmStatusOptions, setCcmStatusOptions] = useState<StatusOption[]>([]);
  const [rpmStatusOptions, setRpmStatusOptions] = useState<StatusOption[]>([]);

  // Sidebar state
  const [showLeftSidebar, setShowLeftSidebar] = useState(true);

  // Notification state - removed dialog state since we're using a separate page

  // Handle incoming state from navigation (when returning from patient details)
  useEffect(() => {
    if (location.state?.view) {
      setCurrentView(location.state.view);
      // Clear the state to prevent it from affecting future navigations
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.state]);

  // Set view based on current route
  useEffect(() => {
    if (location.pathname === '/cs-followup-patients') {
      setCurrentView('followup');
    } else if (location.pathname === '/cs-deferred-patients') {
      setCurrentView('deferred');
    } else if (location.pathname.includes('/dashboard')) {
      setCurrentView('main');
    }
  }, [location.pathname]);

  // Load status options on mount
  useEffect(() => {
    if (hasuraToken) {
      loadStatusOptions();
    }
  }, [hasuraToken]);

  // Load patients when clinic IDs are available
  useEffect(() => {
    if (hasuraToken && clinicIds.length > 0) {
      loadPatients();
    }
  }, [hasuraToken, clinicIds]);

  // Load patients only when view changes initially, not for status filters
  useEffect(() => {
    if (hasuraToken && currentView !== 'search' && clinicIds.length > 0) {
      loadPatients();
    }
  }, [currentView, clinicIds]);


  // Remove search term effect - no more API calls for search
  // All filtering is now done locally

  // Clear search fields when search type changes
  useEffect(() => {
    setSearchTerm('');
    setSelectedDate(undefined);
    setSelectedCCMStatuses([]);
    setSelectedRPMStatuses([]);
  }, [searchBy]);

  // Reset DOB inputs when Appointment Date is selected
  useEffect(() => {
    if (searchBy === 'Appointment Date' && selectedDate) {
      // Reset any DOB-related state if needed
      // This ensures DOB and Appointment Date don't filter simultaneously
    }
  }, [searchBy, selectedDate]);

  const loadStatusOptions = async () => {
    if (!hasuraToken) return;

    try {
      // Import statusGraphqlService at the top of the file
      const { statusGraphqlService } = await import('@/services/api');
      
      // Use role-based CCM status options for CS users
      const ccmResponse = await statusGraphqlService.getCCMStatusOptionsByRole(hasuraToken, 'CS');
      setCcmStatusOptions(ccmResponse.data?.pep_v2_status_mapping_ccm || []);

      // Use role-based RPM status options for CS users
      const rpmResponse = await statusGraphqlService.getRPMStatusOptionsByRole(hasuraToken, 'CS');
      setRpmStatusOptions(rpmResponse.data?.pep_v2_status_mapping_rpm || []);
    } catch (error) {
      console.error('Error loading status options:', error);
      // Fallback to empty arrays to prevent crashes
      setCcmStatusOptions([]);
      setRpmStatusOptions([]);
    }
  };

  const loadPatients = async () => {
    if (!hasuraToken) return;

    setLoading(true);
    try {
      // Update service context with current auth info
      serviceFactory.updateGlobalContext({
        hasuraToken,
        userId: user?.id,
        token: hasuraToken, // Using hasuraToken for GraphQL queries
        userType: user?.type
      });

      const careSpecialistService = serviceFactory.getService(CareSpecialistService);

      const response = await careSpecialistService.fetchCareSpecialistPatientList(
        clinicIds,
        currentView === 'search' ? 'main' : currentView
      );

      if (response.success && response.data) {
        if (currentView === 'main') {
          setPatients(response.data.pep_vw_care_specialist_patient_list || []);
        } else {
          setPatients(response.data.pep_vw_cs_task_patient_list || []);
        }
      } else {
        throw new Error(response.errors?.join(', ') || 'Failed to load patients');
      }
      setCurrentPage(1); // Reset to first page when data changes
    } catch (error) {
      console.error('Error loading patients:', error);
      toast({
        title: "Error",
        description: "Failed to load patients",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePatientAction = (patient: Patient) => {
    navigate(`/patient-details/${patient.patient_clinic_data_id}?from=cs-dashboard`, {
      state: {
        fromCSView: true,
        fromCSUser: true,
        fromCSFollowUp: currentView === 'followup',
        fromCSDeferred: currentView === 'deferred',
        clinic_id: patient.clinic_id
      }
    });
  };

  const handleSearch = () => {
    // All filtering is now done locally
    // Reset to first page when searching
    setCurrentPage(1);
  };

  const handleCCMStatusChange = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedCCMStatuses(prev => [...prev, status]);
    } else {
      setSelectedCCMStatuses(prev => prev.filter(s => s !== status));
    }
  };

  const handleRPMStatusChange = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedRPMStatuses(prev => [...prev, status]);
    } else {
      setSelectedRPMStatuses(prev => prev.filter(s => s !== status));
    }
  };

  const handleNotificationClick = () => {
    navigate('/transfer-patients');
  };

  const formatDateInClinicTimezone = (dateString: string, clinicTimezone: string) => {
    return formatDateTimeWithTimezone(dateString, clinicTimezone);
  };

  const formatDateInLocalTimezone = (dateString: string) => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatDateTimeWithTimezone(dateString, browserTimezone);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not Set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Consent':
      case 'Consented (Virtual)':
      case 'Enrolled (In-Person)':
        return 'default';
      case 'Follow Up':
        return 'secondary';
      case 'Deferred (CS)':
      case 'Deferred':
      case 'Declined':
        return 'destructive';
      case 'Escalate':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getViewTitle = () => {
    switch (currentView) {
      case 'followup':
        return 'Care Specialist (CS) - Follow Up Patients';
      case 'deferred':
        return 'Care Specialist (CS) - Deferred Patients';
      case 'search':
        return 'Global Patient Search';
      default:
        return 'Care Specialist (CS)';
    }
  };

  // Apply local filtering
  const getFilteredPatients = () => {
    let filtered = [...patients];

    // Apply text search filters (local filtering)
    if (searchTerm && (searchBy === 'name' || searchBy === 'clinic' || searchBy === 'phone' || searchBy === 'provider')) {
      filtered = filtered.filter(patient => {
        switch (searchBy) {
          case 'name':
            return patient.full_name.toLowerCase().includes(searchTerm.toLowerCase());
          case 'clinic':
            return patient.clinic_name.toLowerCase().includes(searchTerm.toLowerCase());
          case 'phone':
            return patient.mobile_phone_number.toLowerCase().includes(searchTerm.toLowerCase());
          case 'provider':
            return patient.provider_name.toLowerCase().includes(searchTerm.toLowerCase());
          default:
            return true;
        }
      });
    }

    // Apply date filters (local filtering) - searchTerm contains date string from CSSearchForm
    if (searchTerm && (searchBy === 'dob' || searchBy === 'appointment')) {
      if (searchBy === 'dob') {
        filtered = filtered.filter(patient => {
          if (!patient.dob) return false;
          const patientDOB = format(new Date(patient.dob), 'MM/dd/yyyy');
          return patientDOB.includes(searchTerm) || searchTerm.includes(patientDOB);
        });
      }

      if (searchBy === 'appointment') {
        filtered = filtered.filter(patient => {
          if (!patient.consented_appointment_date_time) return false;
          const appointmentDate = format(new Date(patient.consented_appointment_date_time), 'MM/dd/yyyy');
          return appointmentDate.includes(searchTerm) || searchTerm.includes(appointmentDate);
        });
      }
    }

    // Handle single status search from CSSearchForm
    if (searchBy === 'ccm_status' && searchTerm) {
      filtered = filtered.filter(patient => patient.ccm_status_value === searchTerm);
    }

    if (searchBy === 'rpm_status' && searchTerm) {
      filtered = filtered.filter(patient => {
        if (searchTerm === 'No Status Assigned') {
          return !patient.rpm_status_value || patient.rpm_status_value.trim() === '';
        }
        return patient.rpm_status_value === searchTerm;
      });
    }

    // Apply multi-select status filters (local filtering) - always apply for ccm_and_rpm_status
    if (selectedCCMStatuses.length > 0) {
      filtered = filtered.filter(patient =>
        selectedCCMStatuses.includes(patient.ccm_status_value)
      );
    }

    if (selectedRPMStatuses.length > 0) {
      filtered = filtered.filter(patient => {
        // Handle "No Status Assigned" case
        if (selectedRPMStatuses.includes('No Status Assigned')) {
          if (!patient.rpm_status_value || patient.rpm_status_value.trim() === '') {
            return true;
          }
        }

        // Check for exact match and also check for potential variations
        const exactMatch = selectedRPMStatuses.includes(patient.rpm_status_value);

        // Check for variations of "Consented (In-Person)"
        const normalizedPatientStatus = patient.rpm_status_value?.trim();
        const hasConsentedInPersonMatch = selectedRPMStatuses.some(selectedStatus => {
          const normalizedSelectedStatus = selectedStatus?.trim();
          return normalizedPatientStatus === normalizedSelectedStatus ||
            (normalizedSelectedStatus === 'Consented (In-Person)' &&
              (normalizedPatientStatus === 'Consented(In-Person)' ||
                normalizedPatientStatus === 'Consented (in-Person)' ||
                normalizedPatientStatus === 'Consented(in-Person)' ||
                normalizedPatientStatus.toLowerCase() === 'consented (in-person)'));
        });

        return exactMatch || hasConsentedInPersonMatch;
      });
    }

    return filtered;
  };

  const filteredPatients = getFilteredPatients();

  // Pagination calculations
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentPatients = filteredPatients.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredPatients.length / rowsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRecordsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Render different views
  if (currentView === 'search') {
    return <GlobalSearch onBack={() => setCurrentView('main')} />;
  }

  const getHeaderTitle = () => {
    switch (currentView) {
      case 'followup':
        return 'Care Specialist (CS) - Follow Up Patients';
      case 'deferred':
        return 'Care Specialist (CS) - Deferred Patients';
      default:
        return 'Care Specialist (CS)';
    }
  };

  return (
    <div className="min-h-screen bg-purple-50">
      {/* Main Content */}
      <div className="flex-1">
        {/* Unified Header */}
        <UnifiedHeader
          title={getHeaderTitle()}
          showBackButton={currentView !== 'main'}
          onBackClick={() => navigate('/dashboard')}
          userType="CS"
          showFollowUp={false}
          showDeferred={false}
          showNotificationBell={true}
          onNotificationClick={handleNotificationClick}
          onSearchClick={currentView === 'main' ? () => setCurrentView('search') : undefined}
        />

        {/* Search and Filter Controls */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between space-x-4">
            <CSSearchForm
              searchBy={searchBy}
              searchTerm={searchTerm}
              onSearchByChange={setSearchBy}
              onSearchTermChange={setSearchTerm}
              onSearch={handleSearch}
              onReload={() => {
                setSearchTerm('');
                setSearchBy('name');
                setSelectedDate(undefined);
                setSelectedCCMStatuses([]);
                setSelectedRPMStatuses([]);
                loadPatients();
              }}
              isLoading={loading}
            />
          </div>

          {/* Status Selection Areas - Using common StatusSelectionComponent */}
          <StatusSelectionComponent
            searchBy={searchBy}
            ccmStatusOptions={ccmStatusOptions}
            rpmStatusOptions={rpmStatusOptions}
            selectedCCMStatuses={selectedCCMStatuses}
            selectedRPMStatuses={selectedRPMStatuses}
            onCCMStatusChange={handleCCMStatusChange}
            onRPMStatusChange={handleRPMStatusChange}
            onResetCCMStatuses={() => setSelectedCCMStatuses([])}
            onResetRPMStatuses={() => setSelectedRPMStatuses([])}
            onResetAllStatuses={() => {
              setSelectedCCMStatuses([]);
              setSelectedRPMStatuses([]);
            }}
          />
        </div>

        {/* Patient Table */}
        <div className="p-6">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="p-3 text-left text-sm font-medium text-gray-700 bg-purple-100">
                        Appointment in<br />Clinic Time
                      </th>
                      <th className="p-3 text-left text-sm font-medium text-gray-700 bg-purple-100">
                        Appointment in<br />Local Time
                      </th>
                      <th className="p-3 text-left text-sm font-medium text-gray-700 bg-purple-100">Name</th>
                      <th className="p-3 text-left text-sm font-medium text-gray-700 bg-purple-100">Date of Birth</th>
                      <th className="p-3 text-left text-sm font-medium text-gray-700 bg-purple-100">Clinic Name</th>
                      <th className="p-3 text-left text-sm font-medium text-gray-700 bg-purple-100">CCM Status</th>
                      <th className="p-3 text-left text-sm font-medium text-gray-700 bg-purple-100">RPM Status</th>
                      <th className="p-3 text-left text-sm font-medium text-gray-700 bg-purple-100">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="text-center py-8">
                          <div className="flex items-center justify-center">
                            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                            Loading patients...
                          </div>
                        </td>
                      </tr>
                    ) : currentPatients.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-gray-500">
                          {patients.length === 0 && !searchTerm && !selectedDate && !selectedDateTime && selectedCCMStatuses.length === 0 && selectedRPMStatuses.length === 0 ?
                            "Congrats! You've cleared all the assigned patients for today." :
                            "No patients found"
                          }
                        </td>
                      </tr>
                    ) : (
                      currentPatients.map((patient, index) => (
                        <tr key={patient.pep_patient_id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="p-3 text-sm">
                            {formatDateInClinicTimezone(patient.consented_appointment_date_time, patient.patient_clinic_timezone)}
                          </td>
                          <td className="p-3 text-sm">
                            {formatDateInLocalTimezone(patient.consented_appointment_date_time)}
                          </td>
                          <td className="p-3 text-sm font-medium">{patient.full_name}</td>
                          <td className="p-3 text-sm">{formatDate(patient.dob)}</td>
                          <td className="p-3 text-sm">{patient.clinic_name}</td>
                          <td className="p-3">
                            {patient.ccm_status_value === 'Follow Up' ? (
                              <Badge variant="secondary">
                                {patient.ccm_status_value}
                              </Badge>
                            ) : (
                              <Badge
                                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${getCCMStatusStyle(patient.ccm_status_value || '')}`}
                              >
                                {patient.ccm_status_value}
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            {patient.rpm_status_value === 'Not Applicable (TTCM)' ? (
                              <Badge variant="destructive">
                                {patient.rpm_status_value}
                              </Badge>
                            ) : (
                              <Badge
                                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${getRPMStatusStyle(patient.rpm_status_value || '')}`}
                              >
                                {patient.rpm_status_value || 'No Status Assigned'}
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePatientAction(patient)}
                              className="h-8 w-8"
                            >
                              <SquarePen className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Common Pagination Component */}
          {filteredPatients.length > 0 && (
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              recordsPerPage={rowsPerPage}
              totalRecords={filteredPatients.length}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={handlePageChange}
              onRecordsPerPageChange={handleRecordsPerPageChange}
              className="border-t border-gray-200 bg-white mt-0"
            />
          )}
        </div>
      </div>

    </div>
  );
};

export default CSPatientList;